/**
 * Start every new page at the top.
 *
 * A single-page app keeps the window's scroll offset across a route change, so
 * a link in the footer of a long page drops you the same distance down the
 * next one. The emergency form is where this bites hardest — it is reached
 * from the footer, and it opened halfway down, past the safety notice that is
 * deliberately the first thing on it.
 *
 * `<ScrollRestoration>` would do this, but it needs a data router and this app
 * mounts `<BrowserRouter>`.
 *
 * Three behaviours, and the distinctions are the point:
 *
 *   - **A new navigation goes to the top.** Following a link means arriving at
 *     the start of what you asked for.
 *   - **Back and forward are left alone.** The browser restores those itself,
 *     and returning to a ticket list you had scrolled through should put you
 *     back where you were rather than at the top of it.
 *   - **A `#hash` wins.** An in-page anchor is an explicit request for a
 *     position, so honour it instead.
 *
 * Keyed on the pathname alone: `/ask?c=<id>` changing its query is the same
 * page continuing, not a new one.
 */

import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router";

export function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === "POP") {
      return;
    }
    if (hash !== "") {
      document.getElementById(hash.slice(1))?.scrollIntoView();
      return;
    }
    // `instant`, not smooth: this is arrival, not movement. Animating it would
    // scroll the new page under the reader for no reason, and users who have
    // asked for reduced motion would get it anyway.
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash, navigationType]);

  return null;
}
